/** @jsxImportSource react */
import { useState } from 'react'
import { Box, Button, IconButton, MenuItem, Popover, Stack, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { OP_LABELS, operatorsFor } from './listModel'
import LookupInput from './LookupInput'
import PersonInput from './PersonInput'

const toRows = (filters) =>
  Object.entries(filters).flatMap(([key, ops]) => Object.entries(ops).map(([op, value]) => ({ key, op, value: String(value) })))

const toFilters = (rows) => {
  const filters = {}
  rows.forEach(({ key, op, value }) => {
    if (!key || value === '' || value == null) return
    filters[key] = { ...(filters[key] || {}), [op]: value }
  })
  return filters
}

const ValueInput = ({ field, op, value, onChange }) => {
  const common = { label: 'Value', value, onChange: (e) => onChange(e.target.value), sx: { minWidth: 150, flex: 1 } }
  if (!field) return <TextField {...common} disabled />
  switch (field.fieldType) {
    case 'LOOKUP':
      return <LookupInput field={field} value={value} onChange={onChange} label="Value" />
    case 'USER':
      return <Box sx={{ minWidth: 150, flex: 1 }}><PersonInput value={value} onChange={onChange} label="Value" /></Box>
    case 'SELECT':
    case 'RADIO':
    case 'MULTI_SELECT':
      return <TextField select {...common}>{field.options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}</TextField>
    case 'CHECKBOX':
      return (
        <TextField select {...common}>
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </TextField>
      )
    case 'DATE':
      return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />
    case 'DATETIME':
      return <TextField {...common} type="datetime-local" InputLabelProps={{ shrink: true }} />
    case 'NUMBER':
    case 'DECIMAL':
    case 'CURRENCY':
    case 'ROLLUP':
      return <TextField {...common} inputProps={{ inputMode: 'decimal' }} />
    default:
      return <TextField {...common} placeholder={op === 'contains' ? 'part of the text' : ''} />
  }
}

/** Popover to edit all filters at once; changes apply on "Apply". */
const FilterBuilder = ({ anchorEl, open, onClose, fields, filters, onApply }) => {
  const [rows, setRows] = useState(() => toRows(filters))
  const byKey = Object.fromEntries(fields.map((f) => [f.fieldKey, f]))

  const update = (index, patch) => setRows((prev) => prev.map((row, i) => {
    if (i !== index) return row
    const next = { ...row, ...patch }
    if (patch.key !== undefined) {
      const field = byKey[patch.key]
      next.op = operatorsFor(field)[0]
      next.value = ''
    }
    return next
  }))

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      TransitionProps={{ onEnter: () => setRows(toRows(filters)) }}
      slotProps={{ paper: { sx: { p: 2, width: { xs: 'calc(100vw - 32px)', sm: 620 } } } }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Filters</Typography>
      <Stack spacing={1.5}>
        {rows.length === 0 && <Typography variant="body2" color="text.secondary">No filters. Add one to narrow the list.</Typography>}
        {rows.map((row, index) => {
          const field = byKey[row.key]
          return (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <TextField select label="Field" value={row.key} onChange={(e) => update(index, { key: e.target.value })} sx={{ minWidth: 150, flex: 1 }}>
                {fields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
              </TextField>
              <TextField select label="Is" value={row.op} onChange={(e) => update(index, { op: e.target.value })} disabled={!field} sx={{ width: 120 }}>
                {(field ? operatorsFor(field) : ['eq']).map((op) => (
                  <MenuItem key={op} value={op}>{field?.fieldType === 'MULTI_SELECT' ? 'includes' : OP_LABELS[op]}</MenuItem>
                ))}
              </TextField>
              <ValueInput field={field} op={row.op} value={row.value} onChange={(value) => update(index, { value })} />
              <IconButton onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))} aria-label="Remove filter">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )
        })}
      </Stack>
      <Box sx={{ display: 'flex', mt: 2, gap: 1 }}>
        <Button startIcon={<AddIcon />} onClick={() => setRows((prev) => [...prev, { key: fields[0]?.fieldKey || '', op: operatorsFor(fields[0] || {})[0], value: '' }])} disabled={fields.length === 0}>
          Add filter
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={() => { setRows([]); onApply({}); onClose() }}>Clear all</Button>
        <Button variant="contained" onClick={() => { onApply(toFilters(rows)); onClose() }}>Apply</Button>
      </Box>
    </Popover>
  )
}

export default FilterBuilder
